import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  Crown,
  ShoppingCart,
  Ticket,
  Trash2,
  TrendingUp,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import { useCart, formatInr } from "@/lib/cart";
import { COURSES_LIST, premiumValueList } from "@/lib/data";
import { GST_RATE, PRICING, extractGst, usePricing } from "@/lib/pricing";
import { startCartCheckout } from "@/lib/checkout";
import {
  readPendingCoupon,
  clearPendingCoupon,
} from "@/lib/coupon-capture";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  usePricing();
  const { cart, removeCourse, clear } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [coupon, setCoupon] = useState(null); // { code, discount_paise, final_amount_paise }
  const navigate = useNavigate();

  const titleFor = (slug) =>
    COURSES_LIST.find((c) => c.slug === slug)?.title || slug;
  const coverFor = (slug) =>
    COURSES_LIST.find((c) => c.slug === slug)?.cover;

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // If cart price changes (e.g. user adds/removes a course), clear any applied coupon
  // because the discount was calculated against the old amount.
  if (coupon && coupon._sourcePrice !== cart.price) {
    setTimeout(() => setCoupon(null), 0);
  }

  // All displayed prices are FINAL, GST-INCLUSIVE amounts.
  const couponDiscountRupees = coupon ? coupon.discount_paise / 100 : 0;
  const finalPrice = coupon ? coupon.final_amount_paise / 100 : cart.price;
  const gstRupees = coupon
    ? coupon.gst_paise / 100
    : extractGst(cart.price).gst;
  const taxableRupees = finalPrice - gstRupees;

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) {
      toast.error("Enter a coupon code");
      return;
    }
    setCouponLoading(true);
    try {
      const { data } = await axios.post(`${API}/coupons/validate`, {
        code,
        amount_paise: cart.price * 100,
        package_id: cart.packageId,
      });
      setCoupon({ ...data, _sourcePrice: cart.price });
      toast.success(`Coupon ${data.code} applied — saving ${formatInr(data.discount_paise / 100)}`);
      setCouponInput("");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Invalid coupon";
      toast.error(detail);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    toast("Coupon removed");
  };

  // Auto-apply coupon captured from URL params (e.g. ?coupon=LAUNCH50 or ?utm=launch).
  // Runs once per cart-price change while there's a pending code and no coupon
  // currently applied. On success, the code is consumed from sessionStorage.
  const autoApplyTriedRef = useRef(false);
  useEffect(() => {
    if (coupon || cart.isEmpty) return;
    const pending = readPendingCoupon();
    if (!pending || autoApplyTriedRef.current === pending) return;
    autoApplyTriedRef.current = pending;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/coupons/validate`, {
          code: pending,
          amount_paise: cart.price * 100,
          package_id: cart.packageId,
        });
        setCoupon({ ...data, _sourcePrice: cart.price });
        toast.success(
          `Coupon ${data.code} auto-applied — saving ${formatInr(
            data.discount_paise / 100,
          )}`,
        );
        clearPendingCoupon();
      } catch (_) {
        // Invalid / expired campaign code — drop it silently so we don't nag users.
        clearPendingCoupon();
      }
    })();
  }, [cart.isEmpty, cart.price, cart.packageId, coupon]);

  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);

  const onCheckout = async () => {
    if (!consent) {
      setConsentError(true);
      toast.error("Please accept the Terms & Conditions, Privacy Policy and Refund Policy to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await startCartCheckout({
        courses: cart.courses,
        name: form.name,
        email: form.email,
        phone: form.phone,
        couponCode: coupon?.code,
        consentAccepted: true,
        consentTimestamp: new Date().toISOString(),
      });
      if (res.ok) {
        clear();
        setTimeout(() => navigate("/"), 800);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="cart-page"
      className="min-h-screen w-full max-w-full overflow-x-clip bg-[#F8FAFC] dark:bg-[#020617] text-[#1E293B] dark:text-[#F8FAFC]"
    >
      <Navigation />

      <section className="pt-28 pb-20 sm:pt-32 sm:pb-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link
            to="/"
            data-testid="cart-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-[#0F172A] dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Continue browsing
          </Link>

          <div className="mt-6 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
                Your Cart
              </span>
              <h1 className="font-display mt-2 text-4xl sm:text-5xl tracking-tight font-semibold text-[#0F172A] dark:text-white">
                Review & Checkout
              </h1>
            </div>
            {!cart.isEmpty && (
              <button
                type="button"
                onClick={clear}
                data-testid="cart-clear-all"
                className="text-xs text-slate-500 hover:text-rose-500 inline-flex items-center gap-1"
              >
                <Trash2 className="h-3.5 w-3.5" /> Clear cart
              </button>
            )}
          </div>

          {cart.isEmpty ? (
            <div
              data-testid="cart-empty"
              className="mt-12 rounded-3xl border border-dashed border-slate-300 dark:border-white/10 p-14 text-center"
            >
              <ShoppingCart className="h-10 w-10 mx-auto text-slate-400" />
              <h2 className="mt-5 font-display text-xl font-semibold text-[#0F172A] dark:text-white">
                Your cart is empty
              </h2>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Add a course to get started — packages auto-apply as you add.
              </p>
              <Link
                to="/#courses"
                data-testid="cart-browse-courses"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform"
              >
                Browse Courses <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="mt-10 grid lg:grid-cols-3 gap-8">
              {/* LEFT: Selected Courses */}
              <div className="lg:col-span-2 space-y-5">
                {cart.packageName && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="cart-package-banner"
                    className={`rounded-2xl border p-5 ${
                      cart.packageId === "premium"
                        ? "bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#06B6D4] text-white border-[#06B6D4]/40"
                        : "bg-emerald-500/10 border-emerald-500/30 text-emerald-800 dark:text-emerald-200"
                    }`}
                  >
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold">
                      {cart.packageId === "premium" ? (
                        <>
                          <Crown className="h-3.5 w-3.5" /> Complete Transformation
                        </>
                      ) : (
                        <>🎉 Auto-applied</>
                      )}
                    </div>
                    <div className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight">
                      {cart.packageName} Automatically Applied
                    </div>
                    {cart.savings > 0 && (
                      <div className="mt-1 text-sm opacity-90">
                        You saved <strong>{formatInr(cart.savings)}</strong> vs buying individually.
                      </div>
                    )}
                  </motion.div>
                )}

                {cart.packageId === "premium" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    data-testid="cart-value-breakdown"
                    className="rounded-2xl border border-[#06B6D4]/30 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#0F172A] text-white p-6 sm:p-7 relative overflow-hidden"
                  >
                    <div className="absolute inset-0 gradient-mesh opacity-20 pointer-events-none" />
                    <div className="absolute -bottom-24 -right-24 h-60 w-60 rounded-full bg-[#06B6D4]/15 blur-3xl pointer-events-none" />
                    <div className="relative">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#06B6D4] font-semibold">
                        <Sparkles className="h-3 w-3" /> How you save{" "}
                        {formatInr(PRICING.premiumActualValue - finalPrice)}
                      </div>
                      <h3 className="mt-1.5 font-display text-lg sm:text-xl font-semibold tracking-tight">
                        Complete value breakdown
                      </h3>

                      <ul className="mt-5 space-y-2 text-sm">
                        {premiumValueList().map((it) => (
                          <li
                            key={it.label}
                            className="flex items-center justify-between gap-3 text-slate-200"
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <Check className="h-3.5 w-3.5 flex-none text-emerald-400" />
                              <span className="truncate">{it.label}</span>
                            </span>
                            <span className="font-medium tabular-nums whitespace-nowrap">
                              {formatInr(it.value)}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-5 pt-4 border-t border-white/15 space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-300">Actual value</span>
                          <span className="font-display text-lg font-semibold tabular-nums line-through text-slate-300">
                            {formatInr(PRICING.premiumActualValue)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-300">You pay today</span>
                          <span className="font-display text-lg font-semibold tabular-nums text-white">
                            {formatInr(finalPrice)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between pt-2">
                          <span className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">
                            Total Savings
                          </span>
                          <motion.span
                            animate={{ scale: [1, 1.04, 1] }}
                            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 px-3 py-1.5 font-display text-base font-bold tabular-nums"
                          >
                            <Sparkles className="h-3.5 w-3.5" />{" "}
                            {formatInr(PRICING.premiumActualValue - finalPrice)} ·{" "}
                            {Math.round(
                              ((PRICING.premiumActualValue - finalPrice) / PRICING.premiumActualValue) * 100,
                            )}
                            % OFF
                          </motion.span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {cart.nextTier && (cart.nextTier.saveAmount || cart.nextTier.saveFromCurrent) && (
                  <div
                    data-testid="cart-upgrade-prompt"
                    className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5 text-amber-900 dark:text-amber-200"
                  >
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold">
                      <TrendingUp className="h-3.5 w-3.5" />
                      {cart.n === PRICING.maxCartCourses - 1 ? "🏆 Almost there" : "🔥 Smart upgrade"}
                    </div>
                    <div className="mt-1.5 text-base sm:text-lg font-semibold">
                      {cart.nextTier.label}
                    </div>
                    {cart.nextTier.saveFromCurrent > 0 && (
                      <div className="mt-1 text-sm">
                        Save <strong>{formatInr(cart.nextTier.saveFromCurrent)}</strong> instantly by adding 1 more.
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  {cart.courses.map((slug) => (
                    <motion.div
                      key={slug}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      data-testid={`cart-row-${slug}`}
                      className="flex items-center gap-4 rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-3 sm:p-4"
                    >
                      <img
                        src={coverFor(slug)}
                        alt=""
                        className="h-16 w-16 sm:h-20 sm:w-20 rounded-xl object-cover flex-none"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm sm:text-base font-semibold text-[#0F172A] dark:text-white">
                          {titleFor(slug)}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Individual price: {formatInr(PRICING.coursePrice)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCourse(slug)}
                        data-testid={`cart-remove-${slug}`}
                        className="h-9 w-9 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
                        aria-label="Remove"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>

                <Link
                  to="/#courses"
                  data-testid="cart-add-more"
                  className="inline-flex items-center gap-1.5 text-sm text-[#2563EB] dark:text-[#06B6D4] font-semibold hover:underline"
                >
                  <Sparkles className="h-4 w-4" /> Add more courses
                </Link>
              </div>

              {/* RIGHT: Order Summary + Checkout */}
              <div className="lg:col-span-1">
                <div
                  data-testid="cart-summary"
                  className="rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.04] p-6 sticky top-28"
                >
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
                    Order Summary
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex items-center justify-between text-slate-600 dark:text-slate-400">
                      <span>
                        {cart.n} course{cart.n > 1 ? "s" : ""}
                      </span>
                      <span className={cart.savings > 0 ? "line-through" : ""}>
                        {formatInr(cart.original)}
                      </span>
                    </div>
                    {cart.savings > 0 && (
                      <div
                        data-testid="cart-discount-row"
                        className="flex items-center justify-between text-emerald-600 dark:text-emerald-400"
                      >
                        <span>Package discount</span>
                        <span>− {formatInr(cart.savings)}</span>
                      </div>
                    )}
                    {coupon && (
                      <div
                        data-testid="cart-coupon-row"
                        className="flex items-center justify-between text-emerald-600 dark:text-emerald-400"
                      >
                        <span className="inline-flex items-center gap-1">
                          <Ticket className="h-3.5 w-3.5" />
                          Coupon {coupon.code}
                        </span>
                        <span>− {formatInr(couponDiscountRupees)}</span>
                      </div>
                    )}

                    {/* Taxable value (post-coupon, ex-GST) */}
                    <div
                      data-testid="cart-subtotal-row"
                      className="flex items-center justify-between pt-2 mt-2 border-t border-dashed border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 font-medium"
                    >
                      <span>Taxable value</span>
                      <span>{formatInr(taxableRupees)}</span>
                    </div>

                    {/* GST — already included in the price shown */}
                    <div
                      data-testid="cart-gst-row"
                      className="flex items-center justify-between text-slate-600 dark:text-slate-400"
                    >
                      <span>GST ({Math.round(GST_RATE * 100)}%) included</span>
                      <span>{formatInr(gstRupees)}</span>
                    </div>
                  </div>

                  {/* Coupon input */}
                  <div className="mt-5 pt-4 border-t border-slate-200 dark:border-white/10">
                    {coupon ? (
                      <div
                        data-testid="cart-coupon-applied"
                        className="flex items-center justify-between gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-3 py-2.5"
                      >
                        <span className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                          <BadgeCheck className="h-4 w-4" />
                          {coupon.code} applied
                        </span>
                        <button
                          type="button"
                          onClick={removeCoupon}
                          data-testid="cart-coupon-remove"
                          aria-label="Remove coupon"
                          className="h-7 w-7 inline-flex items-center justify-center rounded-full text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/15"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                          <input
                            value={couponInput}
                            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                            onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                            data-testid="cart-coupon-input"
                            placeholder="Have a coupon?"
                            className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.04] pl-8 pr-3 py-2.5 text-sm uppercase tracking-wider font-medium focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={applyCoupon}
                          disabled={couponLoading}
                          data-testid="cart-coupon-apply"
                          className="rounded-xl bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2.5 text-xs font-semibold uppercase tracking-wider hover:-translate-y-0.5 transition-transform disabled:opacity-60"
                        >
                          {couponLoading ? "…" : "Apply"}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-white/10 flex items-end justify-between">
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                        Total
                      </span>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                        incl. 18% GST
                      </span>
                    </div>
                    <span
                      data-testid="cart-final-amount"
                      className="font-display text-3xl font-semibold tracking-tight text-[#0F172A] dark:text-white"
                    >
                      {formatInr(finalPrice)}
                    </span>
                  </div>

                  <div className="mt-6 space-y-3">
                    <input
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      data-testid="cart-input-name"
                      placeholder="Full name"
                      className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.04] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                    />
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={onChange}
                      data-testid="cart-input-email"
                      placeholder="Email"
                      className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.04] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                    />
                    <input
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={onChange}
                      data-testid="cart-input-phone"
                      placeholder="Phone number"
                      required
                      minLength={6}
                      className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.04] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                    />
                  </div>

                  {/* Legal consent — must be checked before Pay can proceed */}
                  <label
                    htmlFor="cart-consent"
                    className={`mt-5 flex items-start gap-3 rounded-2xl border p-3.5 cursor-pointer transition-colors ${
                      consentError && !consent
                        ? "border-rose-400 bg-rose-50 dark:bg-rose-500/10"
                        : "border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03] hover:border-slate-300 dark:hover:border-white/20"
                    }`}
                    data-testid="cart-consent-label"
                  >
                    <input
                      id="cart-consent"
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => { setConsent(e.target.checked); if (e.target.checked) setConsentError(false); }}
                      data-testid="cart-consent-checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 dark:border-white/30 text-[#0F172A] focus:ring-2 focus:ring-[#06B6D4]/40"
                    />
                    <span className="text-xs sm:text-[13px] leading-relaxed text-slate-700 dark:text-slate-300">
                      I have read and agree to the{" "}
                      <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" data-testid="consent-link-terms" className="text-[#2563EB] dark:text-[#06B6D4] font-semibold underline-offset-2 hover:underline">Terms &amp; Conditions</Link>
                      ,{" "}
                      <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="consent-link-privacy" className="text-[#2563EB] dark:text-[#06B6D4] font-semibold underline-offset-2 hover:underline">Privacy Policy</Link>
                      {" "}and{" "}
                      <Link to="/refund-policy" target="_blank" rel="noopener noreferrer" data-testid="consent-link-refund" className="text-[#2563EB] dark:text-[#06B6D4] font-semibold underline-offset-2 hover:underline">Refund &amp; Cancellation Policy</Link>
                      {" "}of SkillPreneur Academy.
                    </span>
                  </label>
                  {consentError && !consent && (
                    <div
                      data-testid="cart-consent-error"
                      role="alert"
                      className="mt-2 text-xs font-medium text-rose-600 dark:text-rose-400"
                    >
                      Please accept the Terms &amp; Conditions, Privacy Policy and Refund Policy to continue.
                    </div>
                  )}

                  <button
                    type="button"
                    disabled={submitting}
                    onClick={onCheckout}
                    data-testid="cart-checkout-btn"
                    className="mt-4 w-full inline-flex justify-center items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white bg-gradient-to-r from-[#0F172A] via-[#1E3A8A] to-[#06B6D4] shadow-[0_8px_24px_-8px_rgba(6,182,212,0.55)] hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Processing…" : (
                      <>
                        Pay {formatInr(finalPrice)} <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Secure checkout via Razorpay · Lifetime access
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
